import { useCallback, useEffect, useRef } from "react";
import type { Temporal } from "@js-temporal/polyfill";
import {
  createSearchParams,
  useLocation,
  useSearchParams as useRawSearchParams,
} from "react-router-dom";

import {
  parseArraySearchParam,
  parseBooleanSearchParam,
  parseDateSearchParam,
  parseIntegerSearchParam,
  parseNumericSearchParam,
  parsePlainDateSearchParam,
  parsePlainDateTimeSearchParam,
  parsePlainTimeSearchParam,
  parseStringSearchParam,
  parseZonedDateTimeSearchParam,
  type SearchParamParser,
} from "./search-params-parsers";
import {
  serializeArraySearchParam,
  serializeBooleanSearchParam,
  serializeDateSearchParam,
  serializeDateTimeSearchParam,
  serializeIntegerSearchParam,
  serializeNumericSearchParam,
  serializePlainDateSearchParam,
  serializePlainDateTimeSearchParam,
  serializePlainTimeSearchParam,
  serializeZonedDateTimeSearchParam,
  type SearchParamSerializer,
} from "./search-params-serializers";
import { useNavigate, type NavigateOptions } from "../useNavigate";

export type SearchParamType<T> = {
  readonly parser: SearchParamParser<T>;
  readonly serializer: SearchParamSerializer<T>;
};

type SerializerInput<T> = T extends SearchParamSerializer<infer U> ? U : never;

export type SearchParams<S extends SearchParamsSchema> = Partial<{
  [K in keyof S]: SerializerInput<S[K]["serializer"]>;
}>;

type SearchParamsSchema = Record<string, SearchParamType<any>>;

type SearchParamsState<S extends SearchParamsSchema, D> = {
  [Key in keyof S]:
    | Parameters<S[Key]["serializer"]>[0]
    | (Key extends keyof D
        ? undefined extends D[Key]
          ? undefined
          : never
        : undefined);
};

type SatisfiesParamsSchema<S extends SearchParamsSchema, D> = {
  [Key in keyof D]: Key extends keyof S
    ? Extract<Parameters<S[Key]["serializer"]>[0], D[Key]>
    : never;
};

export const useSearchParams = <
  S extends SearchParamsSchema,
  D extends SatisfiesParamsSchema<S, D>,
>(
  schema: S,
  defaultSearchParams = {} as D,
): [
  SearchParamsState<S, D>,
  (nextParams: SearchParams<S>, options?: NavigateOptions) => void,
] => {
  const mounted = useRef(false);
  const [searchParams] = useRawSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const parsedParams = Object.entries(schema)
    .map(([key, handler]) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const parsedValue = tryParseSearchParams(
        searchParams.getAll(key),
        handler,
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const value = parsedValue ?? defaultSearchParams[key as keyof D];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return [key, value];
    })
    .filter(([_, v]) => v !== undefined);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const typedSearchParams = Object.fromEntries(parsedParams);

  const setTypedSearchParams = useCallback(
    (
      nextParams: SearchParams<S>,
      options: NavigateOptions = { replace: true },
    ) => {
      const params = serializeSearchParams(schema, nextParams).toString();

      navigate(
        {
          ...location,
          search: params ? `?${params}` : undefined,
        },
        options,
      );
    },
    [schema, navigate, location],
  );

  useEffect(() => {
    if (
      mounted.current ||
      !Object.keys(defaultSearchParams).length ||
      !Object.entries(defaultSearchParams).some(
        ([key, value]) => value && !searchParams.has(key),
      )
    ) {
      return;
    }

    const allParams = new URLSearchParams(searchParams);
    const params = serializeSearchParams(
      schema,
      typedSearchParams as SearchParams<S>,
    );

    params.forEach((_, key) => {
      allParams.delete(key);
      params.getAll(key).forEach((value) => {
        allParams.append(key, value);
      });
    });
    navigate(
      {
        ...location,
        search: allParams.toString() ? `?${allParams}` : undefined,
      },
      { replace: true },
    );
    mounted.current = true;
  }, [
    location,
    navigate,
    typedSearchParams,
    searchParams,
    schema,
    defaultSearchParams,
  ]);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return [typedSearchParams, setTypedSearchParams];
};

export const serializeSearchParams = <S extends SearchParamsSchema>(
  schema: S,
  nextParams: SearchParams<S>,
): URLSearchParams => {
  const nextParamsKeys = Object.keys(nextParams);

  const params = nextParamsKeys.reduce((acc, key) => {
    const value = nextParams[key];
    if (value !== undefined) {
      const handler = schema[key];

      if (!handler) {
        return acc;
      }

      const serialized = handler.serializer(value);

      if (serialized) {
        return { ...acc, [key]: serialized };
      }

      return acc;
    }
    return acc;
  }, {});
  return createSearchParams(params);
};

export const tryParseSearchParams = (
  value: string[],
  handler: SearchParamType<any>,
) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return handler.parser(value);
  } catch {
    return undefined;
  }
};

const string: SearchParamType<string> = {
  parser: parseStringSearchParam,
  serializer: (value: string) => value,
};

const boolean: SearchParamType<boolean> = {
  parser: parseBooleanSearchParam,
  serializer: serializeBooleanSearchParam,
};

const integer: SearchParamType<number> = {
  parser: parseIntegerSearchParam,
  serializer: serializeIntegerSearchParam,
};

const number: SearchParamType<number> = {
  parser: parseNumericSearchParam,
  serializer: serializeNumericSearchParam,
};

const date: SearchParamType<Date> = {
  parser: parseDateSearchParam,
  serializer: serializeDateSearchParam,
};

const dateTime: SearchParamType<Date> = {
  parser: parseDateSearchParam,
  serializer: serializeDateTimeSearchParam,
};

const enumArray = <T extends string>(
  e: readonly T[],
): SearchParamType<T[]> => ({
  parser: (value: string | string[]): T[] | undefined => {
    const array = ([] as string[])
      .concat(value)
      .filter((a) => e.includes(a as T)) as T[];
    return array.length ? array : undefined;
  },
  serializer: (value?: T[]) => value as string[],
});

const enumString = <T extends string>(e: readonly T[]): SearchParamType<T> => ({
  parser: (value: string | string[]) => {
    const v = typeof value === "string" ? value : value[0];
    return e.includes(v as T) ? (v as T) : undefined;
  },
  serializer: (value: T) => value as string,
});

const array = <T>(itemType: SearchParamType<T>): SearchParamType<T[]> => ({
  parser: parseArraySearchParam(itemType.parser),
  serializer: serializeArraySearchParam(itemType.serializer),
});

const plainTime: SearchParamType<Temporal.PlainTime> = {
  parser: parsePlainTimeSearchParam,
  serializer: serializePlainTimeSearchParam,
};

const plainDate: SearchParamType<Temporal.PlainDate> = {
  parser: parsePlainDateSearchParam,
  serializer: serializePlainDateSearchParam,
};

const plainDateTime: SearchParamType<Temporal.PlainDateTime> = {
  parser: parsePlainDateTimeSearchParam,
  serializer: serializePlainDateTimeSearchParam,
};

const zonedDateTime: SearchParamType<Temporal.ZonedDateTime> = {
  parser: parseZonedDateTimeSearchParam,
  serializer: serializeZonedDateTimeSearchParam,
};

export const searchParamTypes = {
  string,
  boolean,
  integer,
  number,
  date,
  dateTime,
  array,
  enumArray,
  enumString,
  plainTime,
  plainDate,
  plainDateTime,
  zonedDateTime,
};
